import {
    Card,
    CardBody,
    Typography,
    Button,
    Avatar,
    Input,
    IconButton,
} from "@material-tailwind/react";
import {
    ArrowLongRightIcon,
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    PencilSquareIcon,
    ArrowDownTrayIcon,
    AdjustmentsHorizontalIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon, AdjustmentsVerticalIcon
} from "@heroicons/react/24/outline";
import React, {useEffect, useMemo, useRef, useState} from "react";
import { Link } from "react-router-dom";
import getData from "../../api/axiosInstance";
import axios from "axios";
import {BookmarkIcon, PlusCircleIcon} from "@heroicons/react/24/solid";
import {FunnelIcon} from "@heroicons/react/24/outline";
import {useTheme} from "../common/ThemeContext";
import {isWorker} from "../../utils/auth";

// Dialogs
import AddItemDialog from "./dialogs/AddItemDialog";
import EditItemDialog from "./dialogs/EditItemDialog";
import ExportDialog from "./dialogs/ExportDialog";
import GroupDialog from "./dialogs/GroupDialog";
import ShipmentDialog from "./dialogs/ShipmentDialog";
import ProcessingDialog from "./dialogs/ProcessingDialog";

export default function InventoryDashboard({ fixedName = false }) {
    const workerView = isWorker();

    // state management
    const [itemList, setItemList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editItemId, setEditItemId] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState(['IN_STOCK', 'DISPATCHED']);
    const [isLoading, setIsLoading] = useState(true);
    const [copiedId, setCopiedId] = useState(null);
    const [searchType, setSearchType] = useState('title');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedDamaged, setSelectedDamaged] = useState(null);
    const [selectedReserved, setSelectedReserved] = useState(null);
    const { isDark } = useTheme();
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
    const [isShipmentDialogOpen, setIsShipmentDialogOpen] = useState(false);
    const [isProcessingDialogOpen, setIsProcessingDialogOpen] = useState(false);
    const [processingShipmentData, setProcessingShipmentData] = useState(null);
    const [sortSettings, setSortSettings] = useState({
        key: workerView ? 'title' : 'purchasedDate',
        direction: workerView ? 'asc' : 'desc'
    });

    const ALL_COLUMNS = [
        {
            key: 'name',
            label: 'Name',
            locked: true,
        },
        {
            key: 'status',
            label: 'Status',
            locked: true,
        },
        {
            key: 'expectedSellingPrice',
            label: 'Expected Selling Price',
            locked: false,
        },
        {
            key: 'price',
            label: 'Price',
            locked: false,
        },
        {
            key: 'priceFees',
            label: 'Yug Price Fees',
            locked: false,
        },
        {
            key: 'shippingFees',
            label: 'Yug Shipping Fees',
            locked: false,
        },
        {
            key: 'parcelForwardingFees',
            label: 'Forwarding Parcel Fees',
            locked: false,
        },
        {
            key: 'costAfterFees',
            label: 'Final Price',
            locked: false,
        },
        {
            key: 'sellingPrice',
            label: 'Selling Price',
            locked: false,
        },
        {
            key: 'formatType',
            label: 'Format',
            locked: false,
        },
        {
            key: 'itemCondition',
            label: 'Condition',
            locked: false,
        },
        {
            key: 'id',
            label: 'ID',
            locked: false,
        },
        {
            key: 'yugotransportInternalNumber',
            label: 'Yug Internal Number',
            locked: false,
        },
        {
            key: 'yugotransportOrderNumber',
            label: 'Yug Order Number',
            locked: false,
        },
        {
            key: 'wobOrderNumber',
            label: 'WOB Order Number',
            locked: false,
        },
        {
            key: 'royalMailOrderNumber',
            label: 'RoyalMail Order Number',
            locked: false,
        },
        {
            key: 'purchasedDate',
            label: 'Purchased Date',
            locked: false,
        },
        {
            key: 'availabilityDate',
            label: 'Availability Date',
            locked: false,
        },
        {
            key: 'saleDate',
            label: 'Sale Date',
            locked: false,
        },
    ];

    const WORKER_COLUMN_KEYS = new Set([
        'name',
        'status',
        'expectedSellingPrice',
        'formatType',
    ]);

    const AVAILABLE_COLUMNS = workerView
        ? ALL_COLUMNS.filter((column) => WORKER_COLUMN_KEYS.has(column.key))
        : ALL_COLUMNS;

    const DEFAULT_VISIBLE_COLUMNS = new Set(workerView ? [
        'name',
        'status',
        'expectedSellingPrice',
    ] : [
        'name',
        'status',
        'expectedSellingPrice',
        'costAfterFees',
        'sellingPrice',
        'purchasedDate',
        'availabilityDate',
        'saleDate',
    ]);

    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

    // pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isFirstPage, setIsFirstPage] = useState(true);
    const [isLastPage, setIsLastPage] = useState(false);
    const hasPages = totalPages > 0;
    const displayPage = hasPages ? currentPage + 1 : 0;
    const isFirstPageDisabled = currentPage <= 0;
    const isLastPageDisabled = !hasPages || currentPage >= totalPages - 1;

    // refs
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);
    const mobileFilterRef = useRef(null);
    const [mobileSortOpen, setMobileSortOpen] = useState(false);
    const mobileSortRef = useRef(null);
    const [searchTypeOpen, setSearchTypeOpen] = useState(false);
    const searchTypeRef = useRef(null);
    const mobileSearchTypeRef = useRef(null);
    const columnPickerRef = useRef(null);

    // api configuration
    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);

    const statusLabels = {
        PLACED: "Placed",
        DISPATCHED: "Dispatched",
        RECEIVED_UK: "Received (UK)",
        IN_TRANSIT: "In Transit",
        IN_STOCK: "In Stock",
        SOLD: "Sold",
        CANCELLED: "Cancelled",
        GIVEAWAY: "Giveaway",
        IN_STOCK_PERSONAL: "Personal",
        DISPATCHED_PERSONAL: "Personal Dispatched",
        DAMAGED: "Damaged",
    };

    const searchOptions = workerView
        ? [
            { value: 'title', label: 'Title' },
            { value: 'author', label: 'Author' },
        ]
        : [
            { value: 'title', label: 'Title' },
            { value: 'author', label: 'Author' },
            { value: 'wobOrderNumber', label: 'Wob Order Number' },
            { value: 'royalMailOrderNumber', label: 'Royal Mail Order Number' },
            { value: 'yugotransportInternalNumber', label: 'Yugotransport Internal Number' },
            { value: 'reservedFor', label: 'Reserved For' },
        ];

    const typeOptions = workerView ? ["BOOK"] : ["BOOK", "GAME"];

    const statusOptions = workerView
        ? ["DISPATCHED", "IN_STOCK", "CANCELLED", "GIVEAWAY"]
        : ["DISPATCHED", "IN_STOCK", "SOLD", "CANCELLED", "GIVEAWAY", "IN_STOCK_PERSONAL", "DISPATCHED_PERSONAL"];

    const selectedFilterCount = selectedStatuses.length
        + selectedTypes.length
        + (!workerView && selectedDamaged !== null ? 1 : 0)
        + (selectedReserved !== null ? 1 : 0);

    // event handlers
    const toggleEditDialog = (manga) => {
        setEditItemId(manga.id);
        setIsEditDialogOpen(true);
    };

    const toggleStatus = (status) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const toggleType = (type) => {
        setSelectedTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const toggleDamaged = () => {
        setSelectedDamaged(prev => prev === true ? null : true);
    };

    const toggleReserved = () => {
        setSelectedReserved(prev => prev === true ? null : true);
    };

    const damageFilterOptions = workerView ? [] : [
        { label: 'Damaged', checked: selectedDamaged === true, onChange: toggleDamaged },
        { label: 'Not Damaged', checked: selectedDamaged === false, onChange: () => setSelectedDamaged(prev => prev === false ? null : false) },
    ];

    const reservationFilterOptions = [
        { label: 'Reserved', checked: selectedReserved === true, onChange: toggleReserved },
        { label: 'Not Reserved', checked: selectedReserved === false, onChange: () => setSelectedReserved(prev => prev === false ? null : false) },
    ];

    const handleCopyTitle = (manga) => {
        navigator.clipboard.writeText(manga.title).then(() => {
            setCopiedId(manga.id);
            setTimeout(() => setCopiedId(null), 1500);
        });
    };

    const toggleColumn = (key) => {
        setVisibleColumns(
            (prev) => {
                const column = AVAILABLE_COLUMNS.find((item) => item.key === key);
                if (!column || column.locked) return prev;

                const next = new Set(prev);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            }
        );
    };

    // pagination handlers
    const handleFirstPage = () => {
        if (!hasPages) return;
        setCurrentPage(0);
    };

    const handlePrevPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    const handleLastPage = () => {
        if (!hasPages) return;
        setCurrentPage(totalPages - 1);
    };

    const handleSort = (field) => {
        setSortSettings(prev => {
            if (prev.key === field) {
                return {
                    key: field,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                return {
                    key: field,
                    direction: 'asc'
                };
            }
        });
        setCurrentPage(0);
    };

    const handleSelectAllColumns = () => {
        const nonLockedKeys = AVAILABLE_COLUMNS
            .filter(
                (c) => !c.locked
            )
            .map(
                (c) => c.key
            );
        const allVisible = nonLockedKeys.every(
            (k) => visibleColumns.has(k)
        );
        setVisibleColumns(
            (prev) => {
                const next = new Set(prev);
                if (allVisible) {
                    nonLockedKeys.forEach(
                        (k) => next.delete(k)
                    );
                } else {
                    nonLockedKeys.forEach(
                        (k) => next.add(k)
                    );
                }
                return next;
            }
        );
    };

    // refresh helpers - pozivaju ih dialozi posle uspesne akcije
    const buildParams = (page) => {
        return new URLSearchParams({
            page: page.toString(),
            size: itemsPerPage.toString(),
            sortField: sortSettings.key,
            sortDirection: sortSettings.direction,
            ...(searchTerm && { searchTerm, searchType }),
            ...(selectedTypes.length > 0 && { types: selectedTypes.join(',') }),
            ...(selectedStatuses.length > 0 && { statuses: selectedStatuses.join(',') }),
            ...(selectedDamaged !== null && { isDamaged: selectedDamaged.toString() }),
            ...(selectedReserved !== null && { isReserved: selectedReserved.toString() })
        });
    };

    const applyPageData = (data) => {
        setItemList(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
        setIsFirstPage(data.first);
        setIsLastPage(data.last);
    };

    const refreshList = async () => {
        setCurrentPage(0);
        setIsLoading(true);

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        const endpoint = workerView ? "worker/all" : "all";

        try {
            const response = await axios.get(
                `${API_URL}/purchased_items/${endpoint}?${buildParams(0).toString()}`,
                { headers: requestHeaders }
            );
            applyPageData(response.data);
        } catch (err) {
            console.error("Error refreshing:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const refreshCurrentPage = async () => {
        setIsLoading(true);

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        const endpoint = workerView ? "worker/all" : "all";

        try {
            const response = await axios.get(
                `${API_URL}/purchased_items/${endpoint}?${buildParams(currentPage).toString()}`,
                { headers: requestHeaders }
            );
            applyPageData(response.data);
        } catch (err) {
            console.error("Error refreshing:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // shipment → processing bridge
    const handleStartProcessing = (shipmentData) => {
        setProcessingShipmentData(shipmentData);
        setIsProcessingDialogOpen(true);
    };

    // side effects
    useEffect(() => {
        setIsLoading(true);

        getData(
            `/purchased_items/${workerView ? "worker/all" : "all"}?${buildParams(currentPage).toString()}`,
            requestHeaders,
            (data) => {
                applyPageData(data);
                setIsLoading(false);
            },
            "Error fetching purchased items"
        );
    }, [requestHeaders, currentPage, itemsPerPage, searchTerm, searchType,
        selectedTypes, selectedStatuses, selectedDamaged, selectedReserved, sortSettings, workerView]);

    useEffect(() => {
        const handleClick = (e) => {
            if (
                filterRef.current &&
                mobileFilterRef.current &&
                !filterRef.current.contains(e.target) &&
                !mobileFilterRef.current.contains(e.target)
            ) {
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        const handleClick = (e) => {
            if (
                searchTypeRef.current &&
                mobileSearchTypeRef.current &&
                !searchTypeRef.current.contains(e.target) &&
                !mobileSearchTypeRef.current.contains(e.target)
            ) {
                setSearchTypeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(
        () => {
            const handleClick = (e) => {
                if (
                    columnPickerRef.current &&
                    !columnPickerRef.current.contains(e.target)
                ) {
                    setIsColumnPickerOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClick);
            return () => document.removeEventListener('mousedown', handleClick);
        },
        []
    );

    useEffect(() => {
        const handleClick = (e) => {
            if (mobileSortRef.current && !mobileSortRef.current.contains(e.target)) {
                setMobileSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    function storeScrollPosition() {
        sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    }

    const getStatusBadgeClass = (status) => `text-center inline-flex px-2 py-0.5 text-xs font-semibold rounded-md cursor-pointer
                ${status === "PLACED" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" : ""}
                ${status === "DISPATCHED" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" : ""}
                ${status === "RECEIVED_UK" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : ""}
                ${status === "IN_TRANSIT" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : ""}
                ${status === "IN_STOCK" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
                ${status === "SOLD" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : ""}
                ${status === "CANCELLED" ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" : ""}
                ${status === "GIVEAWAY" ? "bg-black text-white dark:bg-gray-900 dark:text-gray-100" : ""}
                ${status === "IN_STOCK_PERSONAL" ? "bg-green-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300" : ""}
                ${status === "DISPATCHED_PERSONAL" ? "bg-yellow-100 text-lime-800 dark:bg-amber-900/30 dark:text-amber-300" : ""}
                ${status === "DAMAGED" ? "bg-red-300 text-black dark:bg-red-800/40 dark:text-red-200" : ""}
                border border-gray-300 dark:border-gray-600`;

    const getAvailabilityDotClass = (manga) => `w-2.5 h-2.5 rounded-full border shrink-0 ${
        (manga.status === "IN_STOCK" || manga.status === "DISPATCHED")
            ? manga.isDamaged
                ? 'bg-red-500 border-red-500'
                : manga.isReserved
                    ? 'bg-orange-500 border-orange-500'
                    : 'bg-green-500 border-green-500'
            : 'bg-transparent border-gray-400'
    }`;

    const getItemBackgroundClass = (manga) => {
        if (manga.isDamaged) return 'bg-red-50 dark:bg-red-950/30';
        if (manga.isReserved) return 'bg-amber-50/70 dark:bg-amber-950/20';
        return '';
    };

    const getStickyItemBackgroundClass = (manga) => {
        if (manga.isDamaged) return 'lg:bg-red-50 dark:lg:bg-red-950/30';
        if (manga.isReserved) return 'lg:bg-amber-50 dark:lg:bg-amber-950/30';
        return 'lg:bg-white dark:lg:bg-dark-900';
    };

    const reservationBadge = (manga) => manga.isReserved ? (
        <span
            title={manga.reservedFor ? `Reserved for ${manga.reservedFor}` : 'Item is reserved'}
            className="mt-1 inline-flex max-w-full items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
        >
            <BookmarkIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="shrink-0">Reserved</span>
            {manga.reservedFor && <span className="truncate">· {manga.reservedFor}</span>}
        </span>
    ) : null;

    const formatItemDate = (date) => date
        ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date))
        : '/';

    const getSortFieldForColumn = (key) => {
        if (key === 'name') return 'title';
        return key;
    };

    const sortedColumnLabel = AVAILABLE_COLUMNS.find((column) => getSortFieldForColumn(column.key) === sortSettings.key)?.label || 'Sort';

    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative p-3 sm:p-6">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Typography variant="h5" color="blue-gray" className="dark:text-gray-300">
                        Dashboard
                    </Typography>
                    <div className={`grid gap-2 sm:flex ${workerView ? 'grid-cols-1' : 'grid-cols-4'}`}>
                        {/* Export Button */}
                        <button
                            onClick={() => setIsExportDialogOpen(true)}
                            className="mv-primary-button"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Export</span>
                        </button>

                        {!workerView && (
                            <>
                        {/* Add Button */}
                        <button
                            onClick={() => setIsAddDialogOpen(true)}
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Add</span>
                        </button>

                        {/* Add Group Button */}
                        <button
                            onClick={() => setIsGroupDialogOpen(true)}
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Add Group</span>
                        </button>

                        {/* Process Shipment Button */}
                        <button
                            onClick={() => setIsShipmentDialogOpen(true)}
                            className="mv-primary-button"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4 rotate-180" />
                            <span className="hidden md:inline">Process Shipment</span>
                        </button>
                            </>
                        )}
                    </div>
                    {!fixedName && !workerView && (
                        <Link to="/inventory" onClick={storeScrollPosition}>
                            <Button size="sm" variant="text" className="flex gap-2 text-cyan-800">
                                View All
                                <ArrowLongRightIcon strokeWidth={2} className="w-4 h-4" />
                            </Button>
                        </Link>
                    )}
                </div>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                <div className="mt-4 mb-3 grid grid-cols-[minmax(0,1fr)_40px_44px_40px] items-center gap-2 md:hidden">
                    <div className="relative min-w-0">
                        <Input
                            type="search"
                            variant="outlined"
                            label={(() => {
                                const item = searchOptions.find((i) => i.value === searchType);
                                return item ? item.label : "Title";
                            })()}
                            placeholder="Search"
                            containerProps={{ className: "min-w-0" }}
                            className="placeholder:text-blue-gray-300 dark:bg-dark-850"
                            color={isDark ? "white" : ""}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="relative" ref={mobileSearchTypeRef}>
                        <button
                            onClick={() => setSearchTypeOpen(p => !p)}
                            className="mv-icon-button"
                        >
                            <FunnelIcon className="w-4 h-4" />
                        </button>

                        {searchTypeOpen && (
                            <div className="absolute top-full mt-2 right-0 z-50 w-[min(240px,calc(100vw-2rem))] bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Search by</span>
                                <div className="mt-1.5 space-y-1">
                                    {searchOptions.map(item => (
                                        <div
                                            key={item.value}
                                            onClick={() => { setSearchType(item.value); setSearchTypeOpen(false); }}
                                            className={`px-2 py-1.5 rounded cursor-pointer text-sm transition-colors
                             ${searchType === item.value
                                                ? 'bg-cyan-700 dark:bg-cyan-800 text-white'
                                                : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-800'}`}
                                        >
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="relative" ref={mobileFilterRef}>
                        <button
                            onClick={() => setFilterOpen(p => !p)}
                            className="mv-icon-button w-11 gap-1"
                        >
                            <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            <span className="text-xs">({selectedFilterCount})</span>
                        </button>

                        {filterOpen && (
                            <div className="absolute top-full mt-2 right-0 z-50 w-[min(260px,calc(100vw-2rem))] bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Type</span>
                                    <div className="flex gap-1 mt-1.5">
                                        {typeOptions.map(type => (
                                            <button
                                                key={type}
                                                onClick={workerView ? undefined : () => toggleType(type)}
                                                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                                    selectedTypes.includes(type) || (workerView && type === "BOOK")
                                                        ? 'bg-cyan-800 text-white dark:bg-cyan-800'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-100 dark:bg-gray-300 dark:text-gray-800'
                                                }`}
                                            >
                                                {type === "BOOK" ? "Book" : "Game"}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mb-2 border-gray-200 dark:border-gray-600" />

                                {[...damageFilterOptions, ...reservationFilterOptions].map(({ label, checked, onChange }) => (
                                    <div
                                        key={label}
                                        className="flex items-center gap-2 mb-1.5 cursor-pointer select-none"
                                        onClick={onChange}
                                    >
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                            checked ? 'bg-cyan-700 border-cyan-700 dark:bg-cyan-800 dark:border-cyan-800' : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                        }`}>
                                            {checked && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                        </div>
                                        <span className="text-sm text-gray-700 dark:text-white">{label}</span>
                                    </div>
                                ))}

                                <hr className="my-2 border-gray-200 dark:border-gray-600" />

                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</span>
                                    <div className="mt-1.5 grid grid-cols-1 gap-1">
                                        {statusOptions.map(status => (
                                            <div
                                                key={status}
                                                className="flex items-center gap-2 cursor-pointer select-none"
                                                onClick={() => toggleStatus(status)}
                                            >
                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                                    selectedStatuses.includes(status) ? 'bg-cyan-700 border-cyan-700 dark:bg-cyan-800 dark:border-cyan-800' : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                                }`}>
                                                    {selectedStatuses.includes(status) && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                                </div>
                                                <span className="text-sm text-gray-700 dark:text-white">{statusLabels[status]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mt-2 mb-2 border-gray-200 dark:border-gray-600" />

                                <button
                                    onClick={() => { setSelectedTypes([]); setSelectedStatuses([]); setSelectedDamaged(null); setSelectedReserved(null); }}
                                    className="w-full text-center text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-200 py-1"
                                >
                                    Reset all filters
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="relative" ref={mobileSortRef}>
                        <button
                            onClick={() => setMobileSortOpen(p => !p)}
                            className="mv-icon-button"
                        >
                            {sortSettings.direction === 'desc'
                                ? <ArrowLongDownIcon className="w-5 h-5" />
                                : <ArrowLongUpIcon className="w-5 h-5" />}
                        </button>

                        {mobileSortOpen && (
                            <div className="absolute top-full mt-2 right-0 z-50 w-[min(270px,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Sort by</span>
                                    <span className="truncate text-xs text-cyan-800 dark:text-cyan-300">
                                        {sortedColumnLabel} {sortSettings.direction}
                                    </span>
                                </div>
                                <div className="mt-2 space-y-1">
                                    {AVAILABLE_COLUMNS.map((column) => {
                                        const sortField = getSortFieldForColumn(column.key);
                                        const isActive = sortSettings.key === sortField;

                                        return (
                                            <button
                                                key={column.key}
                                                type="button"
                                                onClick={() => handleSort(sortField)}
                                                className={`flex w-full items-center justify-between gap-3 rounded px-2 py-2 text-left text-sm transition-colors ${
                                                    isActive
                                                        ? 'bg-cyan-700 text-white dark:bg-cyan-800'
                                                        : 'text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-dark-800'
                                                }`}
                                            >
                                                <span className="truncate">{column.label}</span>
                                                {isActive && (
                                                    sortSettings.direction === 'desc'
                                                        ? <ArrowLongDownIcon className="h-4 w-4 shrink-0" />
                                                        : <ArrowLongUpIcon className="h-4 w-4 shrink-0" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className={`relative hidden overflow-x-auto h-[calc(100vh-24rem)] md:block ${
                        isLoading ? 'overflow-x-hidden' : ''
                    } scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200
                        dark:scrollbar-thumb-gray-400 dark:scrollbar-track-dark-900`}
                >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-850">
                        <tr>
                            {visibleColumns.has('name') && (
                                <th
                                    className={`px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-850 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[150px] ${
                                        fixedName
                                            ? 'lg:sticky lg:top-0 lg:left-0 lg:bg-gray-50 lg:z-20 group lg:after:content-[""] lg:after:absolute lg:after:top-0 lg:after:right-[-1px] lg:after:h-full lg:after:w-[1px] lg:after:bg-gray-300 dark:lg:after:bg-gray-700 lg:after:z-20'
                                            : ''
                                    }`}
                                    onClick={() => handleSort('title')}
                                >
                                    Name
                                    {sortSettings.key === 'title' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('status') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[115px]"
                                    onClick={() => handleSort('status')}
                                >
                                    Status
                                    {sortSettings.key === 'status' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('expectedSellingPrice') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[139px]"
                                    onClick={() => handleSort('expectedSellingPrice')}
                                >
                                    Expected Selling Price
                                    {sortSettings.key === 'expectedSellingPrice' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('price') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('price')}
                                >
                                    Price
                                    {sortSettings.key === 'price' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('priceFees') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('priceFees')}
                                >
                                    Yug Price Fees
                                    {sortSettings.key === 'priceFees' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('shippingFees') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[137px]"
                                    onClick={() => handleSort('shippingFees')}
                                >
                                    Yug Shipping Fees
                                    {sortSettings.key === 'shippingFees' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('parcelForwardingFees') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[135px]"
                                    onClick={() => handleSort('parcelForwardingFees')}
                                >
                                    Forwarding Parcel Fees
                                    {sortSettings.key === 'parcelForwardingFees' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('costAfterFees') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[115px]"
                                    onClick={() => handleSort('costAfterFees')}
                                >
                                    Final Price
                                    {sortSettings.key === 'costAfterFees' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('sellingPrice') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[139px]"
                                    onClick={() => handleSort('sellingPrice')}
                                >
                                    Selling Price
                                    {sortSettings.key === 'sellingPrice' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('formatType') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[110px]"
                                    onClick={() => handleSort('formatType')}
                                >
                                    Format
                                    {sortSettings.key === 'formatType' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('itemCondition') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('itemCondition')}
                                >
                                    Condition
                                    {sortSettings.key === 'itemCondition' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('id') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[65px]"
                                    onClick={() => handleSort('id')}
                                >
                                    ID
                                    {sortSettings.key === 'id' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('yugotransportInternalNumber') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[140px]"
                                    onClick={() => handleSort('yugotransportInternalNumber')}
                                >
                                    Yug Internal Number
                                    {sortSettings.key === 'yugotransportInternalNumber' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('yugotransportOrderNumber') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[121px]"
                                    onClick={() => handleSort('yugotransportOrderNumber')}
                                >
                                    Yug Order Number
                                    {sortSettings.key === 'yugotransportOrderNumber' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('wobOrderNumber') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[180px]"
                                    onClick={() => handleSort('wobOrderNumber')}
                                >
                                    WOB Order Number
                                    {sortSettings.key === 'wobOrderNumber' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('royalMailOrderNumber') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[165px]"
                                    onClick={() => handleSort('royalMailOrderNumber')}
                                >
                                    RoyalMail Order Number
                                    {sortSettings.key === 'royalMailOrderNumber' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('purchasedDate') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('purchasedDate')}
                                >
                                    Purchased Date
                                    {sortSettings.key === 'purchasedDate' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('availabilityDate') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('availabilityDate')}
                                >
                                    Availability Date
                                    {sortSettings.key === 'availabilityDate' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                            {visibleColumns.has('saleDate') && (
                                <th
                                    className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[120px]"
                                    onClick={() => handleSort('saleDate')}
                                >
                                    Sale Date
                                    {sortSettings.key === 'saleDate' && (
                                        sortSettings.direction === 'desc'
                                            ? <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                            : <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800 dark:text-cyan-100" />
                                    )}
                                </th>
                            )}
                        </tr>
                        </thead>

                        {isLoading ? (
                            <tbody className="bg-white dark:bg-dark-900 h-[calc(100vh-24rem)]">
                            <tr>
                                <td colSpan={20} className="px-4 py-1.5">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            {/* Animated Arcado Logo */}
                                            <div className="relative w-24 h-24">
                                                <img
                                                    src="/images/arcado-logo-solo-circle.png"
                                                    alt="Arcado Logo"
                                                    className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                                />
                                                <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                            </div>

                                            {/* Loading text */}
                                            <div className="text-center">
                                                <Typography variant="h6" className="text-gray-800 dark:text-gray-500 font-bold">
                                                    Loading your inventory...
                                                </Typography>
                                                <Typography variant="small" className="text-gray-500 dark:text-gray-200 mt-1">
                                                    We're preparing something awesome for you!
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-300 dark:divide-gray-700">
                            {itemList.map((manga) => (
                                <tr key={manga.id} className={getItemBackgroundClass(manga)}>
                                    {visibleColumns.has('name') && (
                                        <td
                                            className={`px-4 py-1.5 whitespace-nowrap relative ${
                                                fixedName
                                                    ? `lg:sticky lg:left-0 ${getStickyItemBackgroundClass(manga)}`
                                                    : ''
                                            }`}
                                        >
                                            <div className="flex items-center">
                                                {!workerView && (
                                                    <PencilSquareIcon
                                                        onClick={() => toggleEditDialog(manga)}
                                                        className="w-4 h-4 text-cyan-900 dark:text-cyan-200 cursor-pointer mr-2"
                                                    />
                                                )}
                                                {!workerView && (
                                                    <div
                                                        className={`w-3 h-3 rounded-full mr-2 border ${
                                                                (manga.status === "IN_STOCK" || manga.status === "DISPATCHED")
                                                                    ? manga.isDamaged
                                                                        ? 'bg-red-500 border-red-500'
                                                                        : manga.isReserved
                                                                            ? 'bg-orange-500 border-orange-500'
                                                                            : 'bg-green-500 border-green-500'
                                                                    : 'bg-transparent border-gray-400'
                                                            }`}
                                                            title={
                                                                (manga.status === "IN_STOCK" || manga.status === "DISPATCHED")
                                                                    ? manga.isDamaged
                                                                        ? 'Item is damaged'
                                                                        : manga.isReserved
                                                                            ? 'Item is reserved'
                                                                            : 'Item is available'
                                                                    : 'Item is not available'
                                                            }
                                                    ></div>
                                                )}
                                                <div
                                                    className="flex-shrink-0 w-7 h-7 relative cursor-pointer"
                                                    onClick={() => handleCopyTitle(manga)}
                                                >
                                                    <Avatar className="w-6 h-6 rounded-full" src={manga.imageUrl} alt="User" />
                                                    {copiedId === manga.id && (
                                                        <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-50 pointer-events-none">
                                                            Copied!
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="ml-4 relative overflow-hidden max-w-[280px] group">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-300 truncate group-hover:whitespace-normal group-hover:overflow-visible group-hover:max-w-none">
                                                        {manga.title}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-600">
                                                        By {manga.author || '/'}
                                                        {!workerView && (
                                                            <>
                                                                <span className="mx-1 text-gray-800">&bull;</span>{' '}
                                                                <span className="text-teal-800 dark:text-teal-400">
                                                                    {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(manga.purchasedDate))}
                                                                </span>
                                                            </>
                                                        )}
                                                    </p>
                                                    {!workerView && reservationBadge(manga)}
                                                </div>
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.has('status') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <div className={`text-center inline-flex px-2 text-xs font-semibold rounded-md cursor-pointer
                ${manga.status === "PLACED" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" : ""}
                ${manga.status === "DISPATCHED" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" : ""}
                ${manga.status === "RECEIVED_UK" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : ""}
                ${manga.status === "IN_TRANSIT" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : ""}
                ${manga.status === "IN_STOCK" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
                ${manga.status === "SOLD" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : ""}
                ${manga.status === "CANCELLED" ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" : ""}
                ${manga.status === "GIVEAWAY" ? "bg-black text-white dark:bg-gray-900 dark:text-gray-100" : ""}
                ${manga.status === "IN_STOCK_PERSONAL" ? "bg-green-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300" : ""}
                ${manga.status === "DISPATCHED_PERSONAL" ? "bg-yellow-100 text-lime-800 dark:bg-amber-900/30 dark:text-amber-300" : ""}
                ${manga.status === "DAMAGED" ? "bg-red-300 text-black dark:bg-red-800/40 dark:text-red-200" : ""}
                border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}>
                                                {manga.status === "PLACED" && "Placed"}
                                                {manga.status === "DISPATCHED" && "Dispatched"}
                                                {manga.status === "RECEIVED_UK" && "Received UK"}
                                                {manga.status === "IN_TRANSIT" && "In transit"}
                                                {manga.status === "IN_STOCK" && "In stock"}
                                                {manga.status === "SOLD" && "Sold"}
                                                {manga.status === "CANCELLED" && "Cancelled"}
                                                {manga.status === "GIVEAWAY" && "Giveaway"}
                                                {manga.status === "IN_STOCK_PERSONAL" && "Personal"}
                                                {manga.status === "DISPATCHED_PERSONAL" && "Dispatched P"}
                                                {manga.status === "DAMAGED" && "Damaged"}
                                            </div>
                                        </td>
                                    )}
                                    {visibleColumns.has('expectedSellingPrice') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(manga.expectedSellingPriceInRsd)
                                                    ? manga.expectedSellingPriceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600">
                                                {Number.isFinite(manga.costAfterFeesInRsd) && Number.isFinite(manga.expectedSellingPriceInRsd) && manga.costAfterFeesInRsd !== 0
                                                    ? (((manga.expectedSellingPriceInRsd - manga.costAfterFeesInRsd) / manga.costAfterFeesInRsd) * 100).toFixed(2) + '%'
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('price') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(manga.price)
                                                    ? manga.price.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {Number.isFinite(manga.priceInRsd)
                                                    ? manga.priceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('priceFees') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(manga.priceFees)
                                                    ? manga.priceFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600">
                                                {Number.isFinite(manga.priceFeesInRsd)
                                                    ? manga.priceFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('shippingFees') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(manga.shippingFees)
                                                    ? manga.shippingFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600">
                                                {Number.isFinite(manga.shippingFeesInRsd)
                                                    ? manga.shippingFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('parcelForwardingFees') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300">
                                                {Number.isFinite(manga.parcelForwardingFees)
                                                    ? manga.parcelForwardingFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600">
                                                {Number.isFinite(manga.parcelForwardingFeesInRsd)
                                                    ? manga.parcelForwardingFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('costAfterFees') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                                {Number.isFinite(manga.costAfterFees)
                                                    ? manga.costAfterFees.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600 font-bold">
                                                {Number.isFinite(manga.costAfterFeesInRsd)
                                                    ? manga.costAfterFeesInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('sellingPrice') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap">
                                            <p className="text-sm text-gray-900 dark:text-gray-300 font-bold">
                                                {Number.isFinite(manga.sellingPriceInRsd)
                                                    ? manga.sellingPriceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '/'}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-gray-600">
                                                {Number.isFinite(manga.marginPercent)
                                                    ? manga.marginPercent.toFixed(2) + '%'
                                                    : '/'}
                                            </p>
                                        </td>
                                    )}
                                    {visibleColumns.has('formatType') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.formatType}
                                        </td>
                                    )}
                                    {visibleColumns.has('itemCondition') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.itemCondition}
                                        </td>
                                    )}
                                    {visibleColumns.has('id') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.id}
                                        </td>
                                    )}
                                    {visibleColumns.has('yugotransportInternalNumber') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.yugotransportInternalNumber}
                                        </td>
                                    )}
                                    {visibleColumns.has('yugotransportOrderNumber') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.yugotransportOrderNumber}
                                        </td>
                                    )}
                                    {visibleColumns.has('wobOrderNumber') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.wobOrderNumber}
                                        </td>
                                    )}
                                    {visibleColumns.has('royalMailOrderNumber') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.royalMailOrderNumber}
                                        </td>
                                    )}
                                    {visibleColumns.has('purchasedDate') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.purchasedDate
                                                ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(manga.purchasedDate))
                                                : '/'}
                                        </td>
                                    )}
                                    {visibleColumns.has('availabilityDate') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.availabilityDate
                                                ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(manga.availabilityDate))
                                                : '/'}
                                        </td>
                                    )}
                                    {visibleColumns.has('saleDate') && (
                                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-600">
                                            {manga.saleDate
                                                ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(manga.saleDate))
                                                : '/'}
                                        </td>
                                    )}
                                </tr>
                            ))}
                            </tbody>
                        )}
                    </table>
                </div>

                <div className="md:hidden">
                    {isLoading ? (
                        <div className="flex min-h-[44vh] items-center justify-center rounded-lg bg-white dark:bg-dark-900">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                <div className="relative w-20 h-20">
                                    <img
                                        src="/images/arcado-logo-solo-circle.png"
                                        alt="Arcado Logo"
                                        className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                    />
                                    <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                </div>
                                <Typography variant="small" className="text-gray-600 dark:text-gray-300">
                                    Loading items...
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        <div className="max-h-[calc(100vh-20rem)] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-800 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-900">
                            {itemList.map((manga) => (
                                <div
                                    key={manga.id}
                                    className={`px-3 py-3 ${getItemBackgroundClass(manga)}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className="relative h-11 w-11 shrink-0 cursor-pointer"
                                            onClick={() => handleCopyTitle(manga)}
                                        >
                                            <Avatar className="h-11 w-11 rounded-md object-cover" src={manga.imageUrl} alt={manga.title} />
                                            {!workerView && <span className={`absolute -right-1 -top-1 ${getAvailabilityDotClass(manga)}`}></span>}
                                            {copiedId === manga.id && (
                                                <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-50 pointer-events-none">
                                                    Copied!
                                                </span>
                                            )}
                                            </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-200">
                                                    {manga.title}
                                                </p>
                                                {!workerView && (
                                                    <PencilSquareIcon
                                                        onClick={() => toggleEditDialog(manga)}
                                                        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer text-cyan-900 dark:text-cyan-200"
                                                    />
                                                )}
                                            </div>

                                            <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-500">
                                                By {manga.author || '/'}
                                            </p>

                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                <span className={getStatusBadgeClass(manga.status)}>
                                                    {statusLabels[manga.status] || manga.status}
                                                </span>
                                                {!workerView && (
                                                    <span className="text-xs text-teal-800 dark:text-teal-400">
                                                        {formatItemDate(manga.purchasedDate)}
                                                    </span>
                                                )}
                                                {Number.isFinite(manga.expectedSellingPriceInRsd) && (
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                        {manga.expectedSellingPriceInRsd.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                                    </span>
                                                )}
                                                {workerView && manga.formatType && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {manga.formatType}
                                                    </span>
                                                )}
                                                {!workerView && reservationBadge(manga)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div className="mt-4 mb-4 flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
                    {/* LEVA KOLONA - Items per page */}
                    <div className="hidden items-center gap-2 justify-center md:flex md:justify-start">
                        <Typography
                            variant="small"
                            className="text-gray-600 dark:text-gray-400 whitespace-nowrap"
                        >
                            Items per page:
                        </Typography>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(parseInt(e.target.value, 10));
                                setCurrentPage(0);
                            }}
                            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-850 dark:text-gray-400
                 rounded-md px-2 py-1 text-sm w-18
                 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={2000}>2000</option>
                            <option value={5000}>5000</option>
                        </select>
                    </div>

                    {/* SREDINA */}
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400 md:text-left">
                        <span className="md:hidden">{totalElements} items</span>
                        <span className="hidden md:inline">Showing page {displayPage} of {totalPages} ({totalElements} total items)</span>
                    </div>

                    {/* DESNA KOLONA */}
                    <div className="flex justify-center items-center gap-2">
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleFirstPage}
                            disabled={isFirstPageDisabled}
                            className="hidden text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 md:inline-flex
                 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronDoubleLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>

                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handlePrevPage}
                            disabled={isFirstPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200
                 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>

                        <Typography className="dark:text-gray-400 whitespace-nowrap">
                            <strong className="text-gray-900 dark:text-gray-300">
                                {displayPage}
                            </strong>{" "}
                            of{" "}
                            <strong className="text-gray-900 dark:text-gray-300">
                                {totalPages}
                            </strong>
                        </Typography>

                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleNextPage}
                            disabled={isLastPageDisabled}
                            className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200
                 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>

                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleLastPage}
                            disabled={isLastPageDisabled}
                            className="hidden text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 md:inline-flex
                 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                        >
                            <ChevronDoubleRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="hidden items-center mt-5 w-full gap-2 md:flex md:w-max">
                    {/* Search Type Dropdown */}
                    <div className="relative" ref={searchTypeRef}>
                        <button
                            onClick={() => setSearchTypeOpen(p => !p)}
                            className="mv-primary-button gap-1"
                        >
                            <FunnelIcon className="w-4 h-4" />
                        </button>

                        {searchTypeOpen && (
                            <div className="absolute bottom-full mb-2 left-0 z-50 w-[220px] bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Search by</span>
                                <div className="mt-1.5 space-y-1">
                                    {searchOptions.map(item => (
                                        <div
                                            key={item.value}
                                            onClick={() => { setSearchType(item.value); setSearchTypeOpen(false); }}
                                            className={`px-2 py-1.5 rounded cursor-pointer text-sm transition-colors
                             ${searchType === item.value
                                                ? 'bg-cyan-700 dark:bg-cyan-800 text-white'
                                                : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-800'}`}
                                        >
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                        <Input
                            type="search"
                            variant="outlined"
                            label={(() => {
                                const item = searchOptions.find((i) => i.value === searchType);
                                return item ? item.label : "Title";
                            })()}
                            placeholder="Search"
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px]",
                            }}
                            className="placeholder:text-blue-gray-300 dark:bg-dark-850"
                            color={isDark ? "white" : ""}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="relative" ref={filterRef}>
                        <button
                            onClick={() => setFilterOpen(p => !p)}
                            className="mv-primary-button gap-1"
                        >
                            <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            {`(${selectedFilterCount})`}
                        </button>

                        {filterOpen && (
                            <div className="absolute bottom-full mb-2 left-0 z-50 w-[220px] bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">

                                {/* Type */}
                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Type</span>
                                    <div className="flex gap-1 mt-1.5">
                                        {typeOptions.map(type => (
                                            <button
                                                key={type}
                                                onClick={workerView ? undefined : () => toggleType(type)}
                                                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                                    selectedTypes.includes(type) || (workerView && type === "BOOK")
                                                        ? 'bg-cyan-800 text-white dark:bg-cyan-800'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-100 dark:bg-gray-300 dark:text-gray-800'

                                                            // ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-800'
                                                            // : 'bg-gray-900 text-white'
                                                }`}
                                            >
                                                {type === "BOOK" ? "Book" : "Game"}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mb-2 border-gray-200 dark:border-gray-600" />

                                {/* Damaged / Reserved */}
                                {[...damageFilterOptions, ...reservationFilterOptions].map(({ label, checked, onChange }) => (
                                    <div
                                        key={label}
                                        className="flex items-center gap-2 mb-1.5 cursor-pointer select-none"
                                        onClick={onChange}
                                    >
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                            checked ? 'bg-cyan-700 border-cyan-700 dark:bg-cyan-800 dark:border-cyan-800' : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                        }`}>
                                            {checked && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                        </div>
                                        <span className="text-sm text-gray-700 dark:text-white">{label}</span>
                                    </div>
                                ))}

                                <hr className="my-2 border-gray-200 dark:border-gray-600" />

                                {/* Statuses */}
                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</span>
                                    <div className="mt-1.5 space-y-1">
                                        {statusOptions.map(status => (
                                            <div
                                                key={status}
                                                className="flex items-center gap-2 cursor-pointer select-none"
                                                onClick={() => toggleStatus(status)}
                                            >
                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                                    selectedStatuses.includes(status) ? 'bg-cyan-700 border-cyan-700 dark:bg-cyan-800 dark:border-cyan-800' : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                                }`}>
                                                    {selectedStatuses.includes(status) && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                                </div>
                                                <span className="text-sm text-gray-700 dark:text-white">{statusLabels[status]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mt-2 mb-2 border-gray-200 dark:border-gray-600" />

                                <button
                                    onClick={() => { setSelectedTypes([]); setSelectedStatuses([]); setSelectedDamaged(null); setSelectedReserved(null); }}
                                    className="w-full text-center text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-200 py-1"
                                >
                                    Reset all filters
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Column Picker */}
                    <div className="relative" ref={columnPickerRef}>
                        <button
                            onClick={() => setIsColumnPickerOpen((p) => !p)}
                            className="mv-primary-button"
                        >
                            <AdjustmentsVerticalIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Columns</span>
                        </button>

                        {isColumnPickerOpen && (
                            <div className="absolute bottom-full mb-2 left-0 z-50 w-[220px] bg-white dark:bg-dark-850 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">

                                {/* Select All */}
                                <div
                                    className="flex items-center gap-2 mb-1.5 cursor-pointer select-none"
                                    onClick={handleSelectAllColumns}
                                >
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                        AVAILABLE_COLUMNS.filter((c) => !c.locked).map((c) => c.key).every((k) => visibleColumns.has(k))
                                            ? 'bg-cyan-700 border-cyan-700'
                                            : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                    }`}>
                                        {AVAILABLE_COLUMNS.filter((c) => !c.locked).map((c) => c.key).every((k) => visibleColumns.has(k)) && (
                                            <span className="text-white text-[10px] font-bold leading-none">✓</span>
                                        )}
                                    </div>
                                    <span className="text-sm font-normal text-gray-700 dark:text-white">All</span>
                                </div>

                                <hr className="mb-2 border-gray-200 dark:border-gray-600" />

                                <div className="mt-1.5 space-y-1">
                                    {AVAILABLE_COLUMNS.map((col) => (
                                        <div
                                            key={col.key}
                                            className={`flex items-center gap-2 select-none ${
                                                col.locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                                            }`}
                                            onClick={col.locked ? undefined : () => toggleColumn(col.key)}
                                        >
                                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                                                visibleColumns.has(col.key)
                                                    ? 'bg-cyan-700 border-cyan-700 dark:bg-cyan-800 dark:border-cyan-800'
                                                    : 'border-gray-400 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-dark-800'
                                            }`}>
                                                {visibleColumns.has(col.key) && (
                                                    <span className="text-white text-[10px] font-bold leading-none">✓</span>
                                                )}
                                            </div>
                                            <span className="text-sm font-normal text-gray-700 dark:text-white">{col.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Dialogs */}
                {!workerView && (
                    <>
                        <AddItemDialog
                            isOpen={isAddDialogOpen}
                            onClose={() => setIsAddDialogOpen(false)}
                            onSuccess={refreshList}
                            requestHeaders={requestHeaders}
                        />

                        <EditItemDialog
                            isOpen={isEditDialogOpen}
                            onClose={() => setIsEditDialogOpen(false)}
                            onSuccess={refreshCurrentPage}
                            requestHeaders={requestHeaders}
                            itemId={editItemId}
                        />
                    </>
                )}

                <ExportDialog
                    isOpen={isExportDialogOpen}
                    onClose={() => setIsExportDialogOpen(false)}
                    requestHeaders={requestHeaders}
                />

                {!workerView && (
                    <>
                        <GroupDialog
                            isOpen={isGroupDialogOpen}
                            onClose={() => setIsGroupDialogOpen(false)}
                            onSuccess={refreshList}
                            requestHeaders={requestHeaders}
                        />

                        <ShipmentDialog
                            isOpen={isShipmentDialogOpen}
                            onClose={() => setIsShipmentDialogOpen(false)}
                            onStartProcessing={handleStartProcessing}
                        />

                        <ProcessingDialog
                            isOpen={isProcessingDialogOpen}
                            onClose={() => setIsProcessingDialogOpen(false)}
                            onSuccess={refreshList}
                            requestHeaders={requestHeaders}
                            shipmentData={processingShipmentData}
                        />
                    </>
                )}
            </CardBody>
        </Card>
    );
}
